import { useEffect, useRef, useState } from 'react';

function AnimatedCounter({ target, suffix = '', prefix = '' }: { target: number; suffix?: string; prefix?: string }) {
  const [count, setCount] = useState(target);
  const ref = useRef<HTMLDivElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          setCount(0);
          const duration = 2000;
          const startTime = Date.now();
          const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const easeOut = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(easeOut * target));
            if (progress < 1) requestAnimationFrame(animate);
            else setCount(target);
          };
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return (
    <div ref={ref} className="font-display text-4xl md:text-5xl lg:text-6xl font-bold gold-text-gradient">
      {prefix}{count.toLocaleString('es-CO')}{suffix}
    </div>
  );
}

export default function StatsSection() {
  const stats = [
    { value: 500, suffix: 'M+', label: 'USD en proyectos financiados', prefix: '$' },
    { value: 1000, suffix: '+', label: 'Proyectos ejecutados exitosamente', prefix: '' },
    { value: 8, suffix: '', label: 'Países de cobertura en Latinoamérica', prefix: '' },
    { value: 98, suffix: '%', label: 'Tasa de satisfacción de clientes', prefix: '' },
  ];

  return (
    <section className="relative bg-aa-black-soft py-16 md:py-24 overflow-hidden">
      {/* Subtle gold radial accent */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-aa-gold/[0.03] rounded-full blur-3xl pointer-events-none" />

      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Section Label */}
          <div className="text-center mb-12">
            <span className="label-tag">NUESTROS NÚMEROS</span>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-4">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="text-center py-6 px-3 rounded-2xl gold-border-glow glassmorphism"
              >
                <AnimatedCounter target={stat.value} suffix={stat.suffix} prefix={stat.prefix} />
                <p className="text-aa-gray-300 text-xs md:text-sm mt-3 leading-tight">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}